import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';

import styles from './clientGroups.module.scss';
import { config } from '../../api/config';
import { actions } from '../../state';
import {
  centsToMoney,
  isEmpty,
  mapServiceTypes,
  moneyToCents,
  serviceTypes,
} from '../../utils';
import { validationErrors } from './clientGroups.validation';
import {
  Button,
  Checkbox,
  Header,
  Input,
  MultiSelectDropdown,
  Select,
  Title,
} from '../../components';

const CLIENT_TYPE_IDS = {
  private: 91,
  business: 92,
};

const CLIENT_TYPE_VALUES = {
  91: 'private',
  92: 'business',
};

const PAYMENT_METHOD_IDS = {
  creditCard: 481,
  googlePay: 482,
  applePay: 483,
  revolutPay: 484,
  banks: 485,
};

const SERVICE_TYPE_BY_LEGACY = {
  'self-service': 21,
  'tire-blackener': 29,
};

const SERVICE_TYPE_VALUES = serviceTypes;

const SERVICE_OPTIONS = [
  { name: 'Pasirinkti', value: '' },
  ...serviceTypes.map(type => ({
    name: mapServiceTypes(type),
    value: String(type),
  })),
];

const CONTRACT_TYPE_OPTIONS = [
  { name: 'Sutartiniai', value: 'contract' },
  { name: 'Be sutarties', value: 'no-contract' },
];

const APP_TYPE_OPTIONS = [
  { name: 'Programėlė', value: 'app' },
  { name: 'Be programėlės', value: 'no-app' },
];

const toNumber = value => {
  if (value === '' || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const centsToMoneyInput = value => {
  const numberValue = toNumber(value);
  if (numberValue === null) return '';
  return centsToMoney(numberValue);
};

const moneyInputToCents = value => {
  const numberValue = toNumber(value);
  if (numberValue === null) return null;
  return moneyToCents(numberValue);
};

const toUnixMillis = value => {
  if (value === '' || value === null || value === undefined) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.getTime();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const numeric = Number(trimmed);
    if (Number.isFinite(numeric))
      return numeric < 1e12 ? numeric * 1000 : numeric;
    const parsed = Date.parse(trimmed);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
};

const toDateInputValue = value => {
  const ms = toUnixMillis(value);
  if (ms === null) return '';
  return new Date(ms).toISOString().slice(0, 10);
};

const getLocationId = locationValue => {
  if (!locationValue) return null;
  const numeric = toNumber(locationValue);
  if (numeric) return numeric;
  if (locationValue === 'market-st') return 1;
  if (locationValue === 'oxford-st') return 2;
  return toNumber(locationValue);
};

const getMembershipId = membershipValue => {
  if (!membershipValue) return null;
  const numeric = toNumber(membershipValue);
  if (numeric) return numeric;
  if (membershipValue === 'premium') return 1;
  if (membershipValue === 'standard') return 2;
  return toNumber(membershipValue);
};

const normalizeIdArray = ids =>
  Array.isArray(ids) ? ids.map(toNumber).filter(value => value !== null) : [];

const randomInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pickRandomIds = (items = [], maxCount = 2) => {
  if (!items.length) return [];
  const count = randomInt(1, Math.min(maxCount, items.length));
  const shuffled = [...items].sort(() => Math.random() - 0.5);
  return shuffled
    .slice(0, count)
    .map(item => item.id)
    .filter(Boolean);
};

const mapServiceGroupsFromApi = (group, createServiceGroup, createEmptyRow) => {
  if (Array.isArray(group?.service_groups)) {
    return group.service_groups.map((rows, index) => ({
      ...createServiceGroup(index + 1, index + 1),
      rows: rows.map(row => ({
        ...createEmptyRow(),
        id: row.id ?? null,
        serviceType:
          row.service_type !== undefined && row.service_type !== null
            ? String(row.service_type)
            : '',
        sumFrom: centsToMoneyInput(row.sum_from),
        sumTo: centsToMoneyInput(row.sum_to),
        useCount: row.use_count ?? '',
      })),
    }));
  }

  if (Array.isArray(group?.serviceGroups)) {
    return group.serviceGroups.map((serviceGroup, index) => ({
      ...createServiceGroup(serviceGroup.id || index + 1, index + 1),
      rows: (serviceGroup.rows || []).map(row => ({
        ...createEmptyRow(),
        id: row.id ?? null,
        serviceType:
          row.service_type !== undefined && row.service_type !== null
            ? String(row.service_type)
            : SERVICE_TYPE_BY_LEGACY[row.service] !== undefined
            ? String(SERVICE_TYPE_BY_LEGACY[row.service])
            : '',
        sumFrom:
          row.sum_from !== undefined && row.sum_from !== null
            ? centsToMoneyInput(row.sum_from)
            : row.amountFrom ?? '',
        sumTo:
          row.sum_to !== undefined && row.sum_to !== null
            ? centsToMoneyInput(row.sum_to)
            : row.amountTo ?? '',
        useCount: row.use_count ?? row.usageCount ?? '',
      })),
    }));
  }

  return [createServiceGroup(1, 1, 1)];
};

const ClientGroupsAdd = ({
  dispatch,
  history,
  match: { params },
  clientGroups: { selectedGroup, loading },
  locations: { locations },
  memberships: { memberships },
}) => {
  const { id } = params;
  const isEdit = Boolean(id);
  const [hasRequested, setHasRequested] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [clientType, setClientType] = useState('private');
  const [formErrors, setFormErrors] = useState({});
  const [values, setValues] = useState({
    name: '',
    activityMode: 'range',
    dateFrom: '',
    dateTo: '',
    daysFromToday: '',
    virtualTopUpFrom: '',
    virtualTopUpTo: '',
    contractType: '',
    appType: '',
  });
  const [locationIds, setLocationIds] = useState([]);
  const [membershipIds, setMembershipIds] = useState([]);
  const createEmptyRow = useCallback(
    () => ({
      id: null,
      serviceType: '',
      sumFrom: '',
      sumTo: '',
      useCount: '',
    }),
    []
  );
  const buildGroupTitle = useCallback(
    index => `Panaudotų paslaugų grupė ${index}`,
    []
  );
  const createServiceGroup = useCallback(
    (id, index, rowsCount = 1) => ({
      id,
      title: buildGroupTitle(index),
      rows: Array.from({ length: rowsCount }, () => createEmptyRow()),
    }),
    [buildGroupTitle, createEmptyRow]
  );
  const setActivityMode = useCallback(mode => {
    setValues(prev => {
      if (prev.activityMode === mode) return prev;
      return {
        ...prev,
        activityMode: mode,
        ...(mode === 'range'
          ? { daysFromToday: '' }
          : { dateFrom: '', dateTo: '' }),
      };
    });
  }, []);
  const [serviceGroups, setServiceGroups] = useState(() => [
    createServiceGroup(1, 1, 1),
  ]);
  const [paymentMethods, setPaymentMethods] = useState({
    applePay: true,
    googlePay: true,
    revolutPay: true,
    creditCard: true,
    banks: true,
  });

  const activityOptions = [
    { name: 'Data nuo/iki', value: 'range' },
    { name: 'Dienų sk. nuo šiandien', value: 'days' },
  ];
  const [rangeOption, daysOption] = activityOptions;
  const serviceOptions = SERVICE_OPTIONS;
  const availableLocations = useMemo(
    () =>
      (locations || []).map(location => ({
        ...location,
        full_address:
          location.full_address || location.address || String(location.id),
      })),
    [locations]
  );
  const availableMemberships = useMemo(
    () =>
      (memberships || []).map(membership => ({
        ...membership,
        name:
          membership.title_lt ||
          membership.title_en ||
          membership.title_ru ||
          String(membership.id),
      })),
    [memberships]
  );

  const fetchGroup = useCallback(
    groupId => {
      dispatch(actions.clientGroups.getClientGroupAction(groupId));
    },
    [dispatch]
  );

  const fetchLocations = useCallback(() => {
    dispatch(
      actions.locations.getLocationsAction({
        limit: 1000,
        offset: 0,
        order: [
          {
            by: 'full_address',
            order: 'asc',
          },
        ],
      })
    );
  }, [dispatch]);

  const fetchMemberships = useCallback(() => {
    dispatch(
      actions.memberships.getMembershipsAction({
        limit: 1000,
        offset: 0,
        order: [
          {
            by: 'title_lt',
            order: 'asc',
          },
        ],
      })
    );
  }, [dispatch]);

  const clearGroups = useCallback(() => {
    dispatch(actions.clientGroups.clearClientGroupsAction());
  }, [dispatch]);

  const applyGroup = useCallback(
    group => {
      const activityDays =
        group.activity_days_from_today ?? group.activity?.daysFromToday;
      const activityDateFrom = toDateInputValue(
        group.activity_date_from ?? group.activity?.dateFrom ?? ''
      );
      const activityDateTo = toDateInputValue(
        group.activity_date_to ?? group.activity?.dateTo ?? ''
      );
      const nextClientType =
        CLIENT_TYPE_VALUES[group.client_type] || group.clientType || 'private';
      const nextLocationIds =
        group.location_ids?.length > 0
          ? normalizeIdArray(group.location_ids)
          : normalizeIdArray([
              getLocationId(group.location_id ?? group.address),
            ]);
      const nextMembershipIds =
        group.membership_ids?.length > 0
          ? normalizeIdArray(group.membership_ids)
          : normalizeIdArray([
              getMembershipId(group.membership_id ?? group.plan),
            ]);

      setClientType(nextClientType);
      setValues(prev => ({
        ...prev,
        name: group.name || '',
        activityMode: activityDays ? 'days' : 'range',
        dateFrom: activityDateFrom || '',
        dateTo: activityDateTo || '',
        daysFromToday: activityDays ? String(activityDays) : '',
        virtualTopUpFrom: centsToMoneyInput(
          group.virtual_top_up_from ?? group.invoiceFrom
        ),
        virtualTopUpTo: centsToMoneyInput(
          group.virtual_top_up_to ?? group.invoiceTo
        ),
        contractType: (group.contract_type ?? group.contractType) || '',
        appType: (group.app_type ?? group.appType) || '',
      }));
      setLocationIds(nextLocationIds);
      setMembershipIds(nextMembershipIds);
      setServiceGroups(
        mapServiceGroupsFromApi(group, createServiceGroup, createEmptyRow)
      );
      const paymentMethodsList = group.payment_methods || [];
      setPaymentMethods({
        applePay:
          paymentMethodsList.includes(PAYMENT_METHOD_IDS.applePay) ||
          Boolean(group.paymentMethods?.applePay),
        googlePay:
          paymentMethodsList.includes(PAYMENT_METHOD_IDS.googlePay) ||
          Boolean(group.paymentMethods?.googlePay),
        revolutPay:
          paymentMethodsList.includes(PAYMENT_METHOD_IDS.revolutPay) ||
          Boolean(group.paymentMethods?.revolutPay),
        creditCard:
          paymentMethodsList.includes(PAYMENT_METHOD_IDS.creditCard) ||
          Boolean(group.paymentMethods?.creditCard),
        banks:
          paymentMethodsList.includes(PAYMENT_METHOD_IDS.banks) ||
          Boolean(group.paymentMethods?.banks),
      });
    },
    [createEmptyRow, createServiceGroup]
  );

  useEffect(() => {
    if (isEdit) {
      setNotFound(false);
      fetchGroup(id);
    }
    return () => clearGroups();
  }, [clearGroups, fetchGroup, id, isEdit]);

  useEffect(() => {
    dispatch(actions.error.setErrorAction(null));
  }, [dispatch]);

  useEffect(() => {
    fetchLocations();
    fetchMemberships();
  }, [fetchLocations, fetchMemberships]);

  useEffect(() => {
    if (isEdit && loading) {
      setHasRequested(true);
    }
  }, [isEdit, loading]);

  useEffect(() => {
    if (clientType === 'business' && values.activityMode !== 'range') {
      setActivityMode('range');
    }
  }, [clientType, setActivityMode, values.activityMode]);

  useEffect(() => {
    if (!isEdit || !hasRequested || loading) return;
    setNotFound(isEmpty(selectedGroup));
  }, [hasRequested, isEdit, loading, selectedGroup]);

  useEffect(() => {
    if (!isEdit || isEmpty(selectedGroup)) return;
    applyGroup(selectedGroup);
  }, [applyGroup, isEdit, selectedGroup]);

  const serviceGroupsPayload = useMemo(() => {
    return serviceGroups
      .map(group => {
        const rows = group.rows
          .map(row => {
            const serviceType = toNumber(row.serviceType);
            const useCount = toNumber(row.useCount);
            const sumFrom = moneyInputToCents(row.sumFrom);
            const sumTo = moneyInputToCents(row.sumTo);
            const isRowEmpty =
              !serviceType &&
              useCount === null &&
              sumFrom === null &&
              sumTo === null;

            if (isRowEmpty) return null;

            const payload = {};
            if (row.id !== null && row.id !== undefined && row.id !== '') {
              const rowId = toNumber(row.id);
              if (rowId !== null) payload.id = rowId;
            }
            if (serviceType) payload.service_type = serviceType;
            if (useCount !== null) {
              payload.use_count = useCount;
            } else {
              if (sumFrom !== null) payload.sum_from = sumFrom;
              if (sumTo !== null) payload.sum_to = sumTo;
            }

            return payload;
          })
          .filter(Boolean);

        if (rows.length === 0) return null;
        return rows;
      })
      .filter(Boolean);
  }, [serviceGroups]);

  const clearFieldError = useCallback(fieldName => {
    setFormErrors(prev => {
      if (!prev || !prev[fieldName]) return prev;
      return { ...prev, [fieldName]: undefined };
    });
  }, []);

  const clearServiceRowError = useCallback((groupIndex, rowIndex, field) => {
    setFormErrors(prev => {
      if (!prev?.serviceGroups) return prev;
      const groupErrors = prev.serviceGroups[groupIndex];
      if (!groupErrors?.rows?.[rowIndex]) return prev;
      const nextServiceGroups = [...prev.serviceGroups];
      const nextGroup = { ...groupErrors };
      const nextRows = [...nextGroup.rows];
      nextRows[rowIndex] = { ...nextRows[rowIndex], [field]: undefined };
      nextGroup.rows = nextRows;
      nextServiceGroups[groupIndex] = nextGroup;
      return { ...prev, serviceGroups: nextServiceGroups };
    });
  }, []);

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === 'activityMode') {
      setActivityMode(value);
      setFormErrors(prev => ({
        ...prev,
        dateFrom: undefined,
        dateTo: undefined,
        daysFromToday: undefined,
      }));
      return;
    }
    setValues(prev => ({ ...prev, [name]: value }));
    clearFieldError(name);
  };

  const handleServiceChange = (groupIndex, rowIndex, field, value) => {
    setServiceGroups(prev =>
      prev.map((group, gIndex) =>
        gIndex !== groupIndex
          ? group
          : {
              ...group,
              rows: group.rows.map((row, rIndex) =>
                rIndex !== rowIndex ? row : { ...row, [field]: value }
              ),
            }
      )
    );
    clearServiceRowError(groupIndex, rowIndex, field);
  };

  const handleAddServiceGroup = () => {
    setServiceGroups(prev => {
      const maxId = prev.reduce(
        (max, group) => Math.max(max, group.id || 0),
        0
      );
      const nextId = maxId + 1;
      const nextIndex = prev.length + 1;
      return [...prev, createServiceGroup(nextId, nextIndex)];
    });
  };

  const handleRemoveServiceGroup = groupIndex => {
    setServiceGroups(prev => {
      if (prev.length <= 1) return prev;
      const nextGroups = prev.filter((_, index) => index !== groupIndex);
      return nextGroups.map((group, index) => ({
        ...group,
        title: buildGroupTitle(index + 1),
      }));
    });
  };

  const handleAddServiceRow = groupIndex => {
    setServiceGroups(prev =>
      prev.map((group, index) =>
        index !== groupIndex
          ? group
          : {
              ...group,
              rows: [...group.rows, createEmptyRow()],
            }
      )
    );
  };

  const handleRemoveServiceRow = (groupIndex, rowIndex) => {
    setServiceGroups(prev =>
      prev.map((group, index) => {
        if (index !== groupIndex) return group;
        if (group.rows.length <= 1) return group;
        return {
          ...group,
          rows: group.rows.filter((_, rIndex) => rIndex !== rowIndex),
        };
      })
    );
  };

  const togglePaymentMethod = name => {
    setPaymentMethods(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const handleAutofill = () => {
    const nextClientType = Math.random() < 0.5 ? 'private' : 'business';
    const useRange = Math.random() < 0.5;
    const now = Date.now();
    const dateFrom = now;
    const dateTo = now + randomInt(1, 30) * 24 * 60 * 60 * 1000;
    const topUpFrom = randomInt(0, 200);
    const topUpTo = randomInt(Math.max(100, topUpFrom), 500);
    const nextPaymentKeys = Object.keys(PAYMENT_METHOD_IDS);
    const shuffledPayments = [...nextPaymentKeys].sort(
      () => Math.random() - 0.5
    );
    const paymentCount = randomInt(1, shuffledPayments.length);
    const paymentSet = new Set(shuffledPayments.slice(0, paymentCount));

    const buildRandomRow = () => {
      const serviceType =
        SERVICE_TYPE_VALUES[randomInt(0, SERVICE_TYPE_VALUES.length - 1)];
      const useCount = Math.random() < 0.5;
      if (useCount) {
        return {
          id: null,
          serviceType: String(serviceType),
          sumFrom: '',
          sumTo: '',
          useCount: String(randomInt(1, 5)),
        };
      }
      const sumFrom = randomInt(100, 300);
      const sumTo = randomInt(sumFrom, sumFrom + 300);
      return {
        id: null,
        serviceType: String(serviceType),
        sumFrom: String(sumFrom),
        sumTo: String(sumTo),
        useCount: '',
      };
    };

    const groupCount = randomInt(1, 2);
    const nextServiceGroups = Array.from({ length: groupCount }, (_, index) => {
      const rowsCount = randomInt(1, 2);
      const group = createServiceGroup(index + 1, index + 1, rowsCount);
      return {
        ...group,
        rows: Array.from({ length: rowsCount }, () => buildRandomRow()),
      };
    });

    setClientType(nextClientType);
    setValues(prev => ({
      ...prev,
      name: `Test Group ${randomInt(1000, 9999)}`,
      activityMode: useRange ? 'range' : 'days',
      dateFrom: useRange ? toDateInputValue(dateFrom) : '',
      dateTo: useRange ? toDateInputValue(dateTo) : '',
      daysFromToday: useRange ? '' : String(randomInt(1, 30)),
      virtualTopUpFrom: String(topUpFrom),
      virtualTopUpTo: String(topUpTo),
    }));
    setLocationIds(pickRandomIds(availableLocations, 2));
    setMembershipIds(pickRandomIds(availableMemberships, 2));
    setServiceGroups(nextServiceGroups);
    setPaymentMethods(
      nextPaymentKeys.reduce(
        (acc, key) => ({ ...acc, [key]: paymentSet.has(key) }),
        {}
      )
    );
    setFormErrors({});
  };

  const hasErrors = useCallback(errors => {
    if (!errors) return false;
    if (typeof errors === 'string') return true;
    if (Array.isArray(errors)) {
      return errors.some(item => hasErrors(item));
    }
    if (typeof errors === 'object') {
      return Object.values(errors).some(value => hasErrors(value));
    }
    return false;
  }, []);

  const handleSubmit = event => {
    event.preventDefault();
    if (loading) return;
    const error = validationErrors(values, serviceGroups);
    setFormErrors(error);
    if (hasErrors(error)) return;
    const payload = {
      name: values.name,
      client_type: CLIENT_TYPE_IDS[clientType] || CLIENT_TYPE_IDS.private,
      service_groups: serviceGroupsPayload,
      location_ids: locationIds.length ? locationIds : null,
      membership_ids: membershipIds.length ? membershipIds : null,
      virtual_top_up_from: moneyInputToCents(values.virtualTopUpFrom),
      virtual_top_up_to: moneyInputToCents(values.virtualTopUpTo),
      payment_methods: Object.keys(paymentMethods)
        .filter(key => paymentMethods[key])
        .map(key => PAYMENT_METHOD_IDS[key])
        .filter(Boolean),
    };

    if (clientType === 'business') {
      payload.contract_type = values.contractType || null;
      payload.app_type = values.appType || null;
    }

    if (values.activityMode === 'days') {
      const daysFromToday = toNumber(values.daysFromToday);
      if (daysFromToday !== null) {
        payload.activity_days_from_today = daysFromToday;
      }
    } else {
      const dateFrom = toUnixMillis(values.dateFrom);
      const dateTo = toUnixMillis(values.dateTo);
      if (dateFrom !== null) payload.activity_date_from = dateFrom;
      if (dateTo !== null) payload.activity_date_to = dateTo;
    }

    if (isEdit) {
      dispatch(
        actions.clientGroups.editClientGroupAction(id, payload, history)
      );
    } else {
      dispatch(actions.clientGroups.addClientGroupAction(payload, history));
    }
  };

  const isInitialLoading = isEdit && loading && isEmpty(selectedGroup);

  if (isInitialLoading) {
    return (
      <section className={styles.addPage}>
        <Header size="lg">
          <Title>
            {isEdit ? 'Redaguoti klientų grupę' : 'Sukurti klientų grupę'}
          </Title>
        </Header>
        <div className={styles.addCard}>
          <div className={styles.addLoading}>Kraunama...</div>
        </div>
      </section>
    );
  }

  if (notFound) {
    return (
      <section className={styles.addPage}>
        <Header size="lg">
          <Title>Redaguoti klientų grupę</Title>
        </Header>
        <div className={styles.addCard}>
          <div className={styles.addLoading}>Klientų grupė nerasta.</div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.addPage}>
      <Header size="lg">
        <Title>
          {isEdit ? 'Redaguoti klientų grupę' : 'Sukurti klientų grupę'}
        </Title>
      </Header>

      <div className={styles.addCard}>
        <form className={styles.addForm} onSubmit={handleSubmit}>
          <div className={styles.addRadioGroup} role="radiogroup">
            <button
              type="button"
              className={cn(
                styles.addRadioOption,
                clientType === 'private' && styles.addRadioOptionActive
              )}
              onClick={() => setClientType('private')}
              role="radio"
              aria-checked={clientType === 'private'}
            >
              <span className={styles.addRadioCircle}>
                {clientType === 'private' && (
                  <span className={styles.addRadioDot} />
                )}
              </span>
              <span>Privatūs klientai</span>
            </button>
            <button
              type="button"
              className={cn(
                styles.addRadioOption,
                clientType === 'business' && styles.addRadioOptionActive
              )}
              onClick={() => setClientType('business')}
              role="radio"
              aria-checked={clientType === 'business'}
            >
              <span className={styles.addRadioCircle}>
                {clientType === 'business' && (
                  <span className={styles.addRadioDot} />
                )}
              </span>
              <span>Verslo klientai</span>
            </button>
          </div>
          <div className={cn(styles.addRow, styles.addRowNoWrap)}>
            <div className={cn(styles.addField, styles.addFieldLarge)}>
              <label className={styles.addLabel} htmlFor="name">
                Pavadinimas
              </label>
              <Input
                name="name"
                type="text"
                value={values.name}
                onChange={handleInputChange}
                placeholder="Įvesti"
                withLabel={false}
                classes={{ inputGroup: styles.addInputGroup }}
                invalid={Boolean(formErrors?.name)}
                helperText={formErrors?.name}
              />
            </div>
            {clientType === 'business' && (
              <>
                <div className={cn(styles.addField, styles.addFieldSmall)}>
                  <label className={styles.addLabel} htmlFor="contractType">
                    Sutarties tipas
                  </label>
                  <Select
                    name="contractType"
                    options={CONTRACT_TYPE_OPTIONS}
                    selectedValue={values.contractType}
                    onChange={handleInputChange}
                    disabled={true}
                  />
                </div>
                <div className={cn(styles.addField, styles.addFieldSmall)}>
                  <label className={styles.addLabel} htmlFor="appType">
                    Programėlės tipas
                  </label>
                  <Select
                    name="appType"
                    options={APP_TYPE_OPTIONS}
                    selectedValue={values.appType}
                    onChange={handleInputChange}
                    disabled={true}
                  />
                </div>
              </>
            )}
          </div>

          <div className={styles.addSectionTitle}>Aktyvumas dienomis</div>
          {clientType === 'private' && (
            <div
              className={cn(styles.addRadioGroup, styles.addActivityRadioGroup)}
              role="radiogroup"
            >
              <button
                type="button"
                className={cn(
                  styles.addRadioOption,
                  values.activityMode === 'range' && styles.addRadioOptionActive
                )}
                onClick={() => setActivityMode('range')}
                role="radio"
                aria-checked={values.activityMode === 'range'}
              >
                <span className={styles.addRadioCircle}>
                  {values.activityMode === 'range' && (
                    <span className={styles.addRadioDot} />
                  )}
                </span>
                <span>{rangeOption?.name || ''}</span>
              </button>
              <button
                type="button"
                className={cn(
                  styles.addRadioOption,
                  values.activityMode === 'days' && styles.addRadioOptionActive
                )}
                onClick={() => setActivityMode('days')}
                role="radio"
                aria-checked={values.activityMode === 'days'}
              >
                <span className={styles.addRadioCircle}>
                  {values.activityMode === 'days' && (
                    <span className={styles.addRadioDot} />
                  )}
                </span>
                <span>{daysOption?.name || ''}</span>
              </button>
            </div>
          )}
          {(clientType !== 'private' || values.activityMode === 'range') && (
            <div className={cn(styles.addRow, styles.addActivityRow)}>
              <div className={styles.addActivitySmall}>
                <label className={styles.addLabel} htmlFor="dateFrom">
                  Data nuo
                </label>
                <Input
                  name="dateFrom"
                  type="date"
                  value={values.dateFrom}
                  onChange={handleInputChange}
                  onFocus={() => setActivityMode('range')}
                  placeholder={config.DEFAULTS.dateFormat}
                  max="2999-12-31"
                  withLabel={false}
                  classes={{ inputGroup: styles.addInputGroup }}
                  invalid={Boolean(formErrors?.dateFrom)}
                  helperText={formErrors?.dateFrom}
                />
              </div>
              <div className={styles.addActivitySmall}>
                <label className={styles.addLabel} htmlFor="dateTo">
                  Data iki
                </label>
                <Input
                  name="dateTo"
                  type="date"
                  value={values.dateTo}
                  onChange={handleInputChange}
                  onFocus={() => setActivityMode('range')}
                  placeholder={config.DEFAULTS.dateFormat}
                  max="2999-12-31"
                  withLabel={false}
                  classes={{ inputGroup: styles.addInputGroup }}
                  invalid={Boolean(formErrors?.dateTo)}
                  helperText={formErrors?.dateTo}
                />
              </div>
            </div>
          )}
          {clientType === 'private' && values.activityMode === 'days' && (
            <div className={cn(styles.addRow, styles.addActivityRow)}>
              <div className={styles.addActivitySmall}>
                <label className={styles.addLabel} htmlFor="daysFromToday">
                  Dienų sk. nuo šiandien
                </label>
                <Input
                  name="daysFromToday"
                  type="number"
                  value={values.daysFromToday}
                  onChange={handleInputChange}
                  onFocus={() => setActivityMode('days')}
                  placeholder="0"
                  withLabel={false}
                  classes={{ inputGroup: styles.addInputGroup }}
                  invalid={Boolean(formErrors?.daysFromToday)}
                  helperText={formErrors?.daysFromToday}
                />
              </div>
            </div>
          )}

          <div className={styles.addDivider} />

          {serviceGroups.map((group, groupIndex) => (
            <div className={styles.addServiceGroup} key={group.id}>
              <div className={styles.addSectionHeader}>
                <div className={styles.addSectionTitle}>{group.title}</div>
                <Button
                  color="dim"
                  type="button"
                  classes={{ button: styles.addGroupRemoveButton }}
                  onClick={() => handleRemoveServiceGroup(groupIndex)}
                  disabled={serviceGroups.length <= 1}
                >
                  Ištrinti paslaugų grupę
                </Button>
              </div>
              <div className={styles.addServiceRows}>
                {group.rows.map((row, rowIndex) => (
                  <div
                    className={styles.addServiceRow}
                    key={`${group.id}-${rowIndex}`}
                  >
                    <div
                      className={cn(
                        styles.addField,
                        styles.addServiceColumn,
                        formErrors?.serviceGroups?.[groupIndex]?.rows?.[
                          rowIndex
                        ]?.serviceType && styles.fieldError
                      )}
                    >
                      <label className={styles.addLabel}>Paslauga</label>
                      <Select
                        name={`service-${group.id}-${rowIndex}`}
                        options={serviceOptions}
                        selectedValue={row.serviceType}
                        onChange={event =>
                          handleServiceChange(
                            groupIndex,
                            rowIndex,
                            'serviceType',
                            event.target.value
                          )
                        }
                      />
                      {formErrors?.serviceGroups?.[groupIndex]?.rows?.[rowIndex]
                        ?.serviceType && (
                        <span className={styles.error}>
                          {
                            formErrors.serviceGroups[groupIndex].rows[rowIndex]
                              .serviceType
                          }
                        </span>
                      )}
                    </div>
                    <div className={styles.addFieldGrow}>
                      <label className={styles.addLabel}>Suma nuo, EUR</label>
                      <Input
                        name={`amountFrom-${group.id}-${rowIndex}`}
                        type="number"
                        value={row.sumFrom}
                        onChange={event =>
                          handleServiceChange(
                            groupIndex,
                            rowIndex,
                            'sumFrom',
                            event.target.value
                          )
                        }
                        placeholder="Įvesti"
                        withLabel={false}
                        classes={{ inputGroup: styles.addInputGroup }}
                        invalid={Boolean(
                          formErrors?.serviceGroups?.[groupIndex]?.rows?.[
                            rowIndex
                          ]?.sumFrom
                        )}
                        helperText={
                          formErrors?.serviceGroups?.[groupIndex]?.rows?.[
                            rowIndex
                          ]?.sumFrom
                        }
                      />
                    </div>
                    <div className={styles.addFieldGrow}>
                      <label className={styles.addLabel}>Suma iki, EUR</label>
                      <Input
                        name={`amountTo-${group.id}-${rowIndex}`}
                        type="number"
                        value={row.sumTo}
                        onChange={event =>
                          handleServiceChange(
                            groupIndex,
                            rowIndex,
                            'sumTo',
                            event.target.value
                          )
                        }
                        placeholder="Įvesti"
                        withLabel={false}
                        classes={{ inputGroup: styles.addInputGroup }}
                        invalid={Boolean(
                          formErrors?.serviceGroups?.[groupIndex]?.rows?.[
                            rowIndex
                          ]?.sumTo
                        )}
                        helperText={
                          formErrors?.serviceGroups?.[groupIndex]?.rows?.[
                            rowIndex
                          ]?.sumTo
                        }
                      />
                    </div>
                    <div className={styles.addOrColumn}>
                      <div className={styles.addLabelSpacer} />
                      <span className={styles.addOrLabel}>Arba</span>
                    </div>
                    <div className={styles.addFieldGrow}>
                      <label className={styles.addLabel}>Panaudojimų sk.</label>
                      <Input
                        name={`usageCount-${group.id}-${rowIndex}`}
                        type="number"
                        value={row.useCount}
                        onChange={event =>
                          handleServiceChange(
                            groupIndex,
                            rowIndex,
                            'useCount',
                            event.target.value
                          )
                        }
                        placeholder="0"
                        withLabel={false}
                        classes={{ inputGroup: styles.addInputGroup }}
                        invalid={Boolean(
                          formErrors?.serviceGroups?.[groupIndex]?.rows?.[
                            rowIndex
                          ]?.useCount
                        )}
                        helperText={
                          formErrors?.serviceGroups?.[groupIndex]?.rows?.[
                            rowIndex
                          ]?.useCount
                        }
                      />
                    </div>
                    <div className={styles.addRemoveColumn}>
                      <div className={styles.addLabelSpacer} />
                      <button
                        type="button"
                        className={styles.addRemoveButton}
                        onClick={() =>
                          handleRemoveServiceRow(groupIndex, rowIndex)
                        }
                        aria-label="Pašalinti eilutę"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="20"
                          viewBox="0 0 18 20"
                          fill="none"
                          className={styles.addRemoveIcon}
                        >
                          <path
                            d="M17.25 3C17.6642 3 18 3.33579 18 3.75C18 4.16421 17.6642 4.5 17.25 4.5H16.5V18C16.5 18.3978 16.3419 18.7792 16.0605 19.0605C15.7792 19.3419 15.3978 19.5 15 19.5H3C2.60218 19.5 2.22076 19.3419 1.93945 19.0605C1.65815 18.7792 1.5 18.3978 1.5 18V4.5H0.75C0.335786 4.5 0 4.16421 0 3.75C0 3.33579 0.335786 3 0.75 3H17.25ZM3 18H15V4.5H3V18ZM9 6.25C9.41421 6.25 9.75 6.58579 9.75 7V15C9.75 15.4142 9.41421 15.75 9 15.75C8.58579 15.75 8.25 15.4142 8.25 15V7C8.25 6.58579 8.58579 6.25 9 6.25ZM6 7.25C6.41421 7.25 6.75 7.58579 6.75 8V14C6.75 14.4142 6.41421 14.75 6 14.75C5.58579 14.75 5.25 14.4142 5.25 14V8C5.25 7.58579 5.58579 7.25 6 7.25ZM12 7.25C12.4142 7.25 12.75 7.58579 12.75 8V14C12.75 14.4142 12.4142 14.75 12 14.75C11.5858 14.75 11.25 14.4142 11.25 14V8C11.25 7.58579 11.5858 7.25 12 7.25ZM12.75 0C13.1642 0 13.5 0.335786 13.5 0.75C13.5 1.16421 13.1642 1.5 12.75 1.5H5.25C4.83579 1.5 4.5 1.16421 4.5 0.75C4.5 0.335786 4.83579 0 5.25 0H12.75Z"
                            fill="#FF3B30"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className={styles.addLinkButton}
                  onClick={() => handleAddServiceRow(groupIndex)}
                >
                  Arba kita paslauga
                </button>
              </div>
            </div>
          ))}

          <Button
            color="dark"
            type="button"
            classes={{ button: styles.addAddServiceButton }}
            onClick={handleAddServiceGroup}
          >
            Pridėti paslaugą
          </Button>

          <div className={styles.addDivider} />

          <div className={styles.addSectionTitle}>Plovykla ir planas</div>
          <div className={cn(styles.addRow, styles.addRowNoWrap)}>
            <div className={styles.addFieldGrow}>
              <label className={styles.addLabel}>Plovyklos adresas</label>
              {formErrors?.locationIds ? (
                <div className={styles.fieldErrorBox}>
                  <MultiSelectDropdown
                    array={availableLocations}
                    name="full_address"
                    value="id"
                    valueType="number"
                    state={locationIds}
                    setState={setLocationIds}
                    placeholder="Pasirinkti"
                    allText="Visi"
                  />
                </div>
              ) : (
                <MultiSelectDropdown
                  array={availableLocations}
                  name="full_address"
                  value="id"
                  valueType="number"
                  state={locationIds}
                  setState={setLocationIds}
                  placeholder="Pasirinkti"
                  allText="Visi"
                />
              )}
            </div>
            <div className={styles.addFieldGrow}>
              <label className={styles.addLabel}>Aktyvus planas</label>
              {formErrors?.membershipIds ? (
                <div className={styles.fieldErrorBox}>
                  <MultiSelectDropdown
                    array={availableMemberships}
                    name="name"
                    value="id"
                    valueType="number"
                    state={membershipIds}
                    setState={setMembershipIds}
                    placeholder="Pasirinkti"
                    allText="Visi"
                  />
                </div>
              ) : (
                <MultiSelectDropdown
                  array={availableMemberships}
                  name="name"
                  value="id"
                  valueType="number"
                  state={membershipIds}
                  setState={setMembershipIds}
                  placeholder="Pasirinkti"
                  allText="Visi"
                />
              )}
            </div>
            <div className={styles.addFieldGrow}>
              <label className={styles.addLabel}>
                {clientType === 'business'
                  ? 'Sąskaitos suma'
                  : 'Virtualios piniginės pildymas, Eur'}
              </label>
              <div className={styles.addRangeRow}>
                <Input
                  name="virtualTopUpFrom"
                  type="number"
                  value={values.virtualTopUpFrom}
                  onChange={handleInputChange}
                  placeholder="Nuo, Eur"
                  withLabel={false}
                  classes={{ inputGroup: styles.addInputGroup }}
                  invalid={Boolean(formErrors?.virtualTopUpFrom)}
                  helperText={formErrors?.virtualTopUpFrom}
                />
                <Input
                  name="virtualTopUpTo"
                  type="number"
                  value={values.virtualTopUpTo}
                  onChange={handleInputChange}
                  placeholder="Iki, Eur"
                  withLabel={false}
                  classes={{ inputGroup: styles.addInputGroup }}
                  invalid={Boolean(formErrors?.virtualTopUpTo)}
                  helperText={formErrors?.virtualTopUpTo}
                />
              </div>
            </div>
          </div>

          <div className={styles.addSectionTitle}>Mokėjimo metodai</div>
          <div className={styles.addCheckboxList}>
            <Checkbox
              name="applePay"
              label="Apple Pay"
              value="applePay"
              checked={paymentMethods.applePay}
              onChange={() => togglePaymentMethod('applePay')}
              fullLine={true}
              dense={true}
            />
            <Checkbox
              name="googlePay"
              label="Google Pay"
              value="googlePay"
              checked={paymentMethods.googlePay}
              onChange={() => togglePaymentMethod('googlePay')}
              fullLine={true}
              dense={true}
            />
            <Checkbox
              name="revolutPay"
              label="Revolut Pay"
              value="revolutPay"
              checked={paymentMethods.revolutPay}
              onChange={() => togglePaymentMethod('revolutPay')}
              fullLine={true}
              dense={true}
            />
            <Checkbox
              name="creditCard"
              label="Kreditinė kortelė"
              value="creditCard"
              checked={paymentMethods.creditCard}
              onChange={() => togglePaymentMethod('creditCard')}
              fullLine={true}
              dense={true}
            />
            <Checkbox
              name="banks"
              label="Bankai"
              value="banks"
              checked={paymentMethods.banks}
              onChange={() => togglePaymentMethod('banks')}
              fullLine={true}
              dense={true}
            />
          </div>

          <div className={styles.addDivider} />

          <div className={styles.addActions}>
            <Button
              color="outline"
              url={config.APP_PATHS.clientGroups}
              classes={{ button: styles.addActionButton }}
            >
              Atgal
            </Button>
            {/* <Button
              color="dim"
              type="button"
              onClick={handleAutofill}
              classes={{ button: styles.addActionButton }}
            >
              Auto-fill test data
            </Button> */}
            <Button
              color="primary"
              type="submit"
              classes={{ button: styles.addActionButton }}
              disabled={loading}
            >
              {isEdit ? 'Išsaugoti' : 'Sukurti'}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};

const mapStateToProps = state => ({
  clientGroups: state.clientGroups,
  locations: state.locations,
  memberships: state.memberships,
});

ClientGroupsAdd.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
  clientGroups: PropTypes.shape({
    selectedGroup: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  locations: PropTypes.shape({
    locations: PropTypes.array.isRequired,
  }).isRequired,
  memberships: PropTypes.shape({
    memberships: PropTypes.array.isRequired,
  }).isRequired,
};

export default connect(mapStateToProps)(ClientGroupsAdd);
